import { useEffect, useState } from "react";
import type { FxRate, FxCurrency, FxRateSource } from "@/types";
import { colors, semanticColors, radius, spacing, heights, layout, typography, shadows, motion } from "@/styles/tokens";
import { DetailPanel } from "@/components/DetailPanel";
import { ReadOnlySave } from "@/components/ReadOnlySave";

const LABEL: React.CSSProperties = {
  fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
  textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
  fontWeight: typography.fontWeight.medium, marginBottom: spacing[4],
};
const INPUT: React.CSSProperties = {
  width: "100%", padding: `${spacing[6]}px ${spacing[10]}px`, height: heights.input,
  fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  backgroundColor: semanticColors.surface, outline: "none",
  boxSizing: "border-box",
};

const CURRENCIES: FxCurrency[] = ["USD", "EUR", "CNY"];
const SOURCES: FxRateSource[] = ["Customs Notified", "Manual Override"];

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <span style={LABEL}>{children}</span>;
}

function TextField({
  label, testid, value, onChange, placeholder,
}: { label: string; testid: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FieldLabel>{label}</FieldLabel>
      <input data-testid={testid} type="text" value={value} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)} style={INPUT} />
    </div>
  );
}
function NumberField({
  label, testid, value, onChange,
}: { label: string; testid: string; value: number; onChange: (v: number) => void }) {
  const [buf, setBuf] = useState<string>(String(value));
  useEffect(() => { setBuf(String(value)); }, [value]);
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FieldLabel>{label}</FieldLabel>
      <input data-testid={testid} type="number" value={buf}
             onChange={(e) => {
               setBuf(e.target.value);
               const n = parseFloat(e.target.value);
               if (Number.isFinite(n)) onChange(n);
             }}
             style={{ ...INPUT, textAlign: "right", fontVariantNumeric: "tabular-nums" }} />
    </div>
  );
}
function SelectField<T extends string>({
  label, testid, value, options, onChange,
}: { label: string; testid: string; value: T; options: readonly T[]; onChange: (v: T) => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FieldLabel>{label}</FieldLabel>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value as T)} style={INPUT}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
function DateField({
  label, testid, value, onChange,
}: { label: string; testid: string; value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FieldLabel>{label}</FieldLabel>
      <input data-testid={testid} type="date" value={value}
             onChange={(e) => onChange(e.target.value)} style={INPUT} />
    </div>
  );
}
function SourceRadio({ value, onChange }: { value: FxRateSource; onChange: (v: FxRateSource) => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FieldLabel>Source</FieldLabel>
      <div className="flex items-center" style={{ gap: spacing[16], height: heights.input }}>
        {SOURCES.map((o) => (
          <label key={o} data-testid={`radio-source-${o.replace(/\s+/g, "-").toLowerCase()}`}
                 className="flex items-center"
                 style={{ gap: spacing[6], fontSize: typography.fontSize.base, color: semanticColors.textPrimary, cursor: "pointer" }}>
            <input type="radio" name="fxRateSource" checked={value === o}
                   onChange={() => onChange(o)} style={{ accentColor: colors.indigo600 }} />
            {o}
          </label>
        ))}
      </div>
    </div>
  );
}
function ActiveToggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between">
      <FieldLabel>Active</FieldLabel>
      <button data-testid="fxrate-active-toggle" onClick={() => onChange(!value)}
              style={{
                width: layout.toggleTrackWidth, height: heights.pill, padding: spacing[2], borderRadius: radius.full,
                border: "none", cursor: "pointer",
                backgroundColor: value ? colors.indigo600 : semanticColors.textDisabled,
                display: "flex", alignItems: "center",
                justifyContent: value ? "flex-end" : "flex-start",
                transition: `background-color ${motion.fast}`,
              }}>
        <span style={{ width: heights.icon, height: heights.icon, borderRadius: "50%",
                       backgroundColor: colors.white, boxShadow: shadows.toggleKnob }} />
      </button>
    </div>
  );
}

export function FxRateDetailPanel({
  rate, onClose,
}: {
  rate: FxRate | null;
  onClose: () => void;
}) {
  const [form, setForm] = useState<FxRate | null>(rate);
  useEffect(() => { setForm(rate); }, [rate]);
  if (!rate || !form) return null;

  const set = <K extends keyof FxRate>(k: K, v: FxRate[K]) =>
    setForm((cur) => (cur ? { ...cur, [k]: v } : cur));

  return (
    <DetailPanel
      testIdPrefix="fxrate"
      title={`${form.currency} → INR`}
      subtitle={form.id}
      onClose={onClose}
      footer={
        <>
          <button data-testid="fxrate-panel-cancel" className="btn-secondary" onClick={onClose}
                  style={{ height: heights.control, padding: `0 ${spacing[14]}px`, backgroundColor: semanticColors.surface,
                           color: semanticColors.textSecondary, border: `1px solid ${semanticColors.border}`,
                           borderRadius: radius.base, fontSize: typography.fontSize.base, cursor: "pointer" }}>
            Cancel
          </button>
          <ReadOnlySave testId="fxrate-panel-save" />
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: spacing[14] }}>
        <SelectField label="Currency" testid="fxr-currency" value={form.currency}
                     options={CURRENCIES} onChange={(v) => set("currency", v)} />

        <NumberField label="Rate (to INR)" testid="fxr-rate" value={form.rate}
                     onChange={(v) => set("rate", v)} />

        <DateField label="Effective From" testid="fxr-effective-from" value={form.effectiveFrom}
                   onChange={(v) => set("effectiveFrom", v)} />

        <SourceRadio value={form.source} onChange={(v) => set("source", v)} />

        <TextField label="Notes" testid="fxr-notes" value={form.notes}
                   onChange={(v) => set("notes", v)}
                   placeholder="e.g. interim correction ahead of scheduled notification" />

        <ActiveToggle value={form.active} onChange={(v) => set("active", v)} />
      </div>
    </DetailPanel>
  );
}
